/**
 * Equipment-class icon glyphs (UX-004 §2.4). The spec wants a *class* icon for an
 * equipment family, not a generic lucide shape. Glyphs are line art in
 * `currentColor` — they carry identity, never health, so colour must come from the
 * surrounding text, not from the icon (health colour stays on HealthBadge/HealthDot,
 * UX-003 §3.7).
 *
 * Decorative by default (`aria-hidden`), because the class name is almost always
 * beside the icon. Pass `title` to make it a labelled image where it stands alone.
 */
import type { ReactNode } from "react";

export type EquipmentClass =
  | "inverter"
  | "meter"
  | "combiner"
  | "string"
  | "transformer"
  | "sensor"
  | "weather_station"
  | "logger"
  | "gateway"
  | "module"
  | "storage"
  | "breaker"
  | "load"
  | "device";

const CLASS_LABEL: Record<EquipmentClass, string> = {
  inverter: "Inverter",
  meter: "Meter",
  combiner: "Combiner / SCB",
  string: "String",
  transformer: "Transformer",
  sensor: "Sensor",
  weather_station: "Weather station",
  logger: "Logger",
  gateway: "Gateway",
  module: "Module",
  storage: "Storage / BESS",
  breaker: "Breaker",
  load: "Load",
  device: "Device",
};

/** Normalise a free-form device_class string to a known class (fallback: device). */
export function equipmentClass(raw?: string | null): EquipmentClass {
  if (!raw) return "device";
  const k = raw.toLowerCase().replace(/[\s-]+/g, "_");
  if (k in CLASS_LABEL) return k as EquipmentClass;
  if (k.includes("invert")) return "inverter";
  if (k.includes("combin") || k === "scb") return "combiner";
  if (k.includes("string")) return "string";
  if (k.includes("transform")) return "transformer";
  // Weather/irradiance first: "pyranometer" contains "meter".
  if (k.includes("weather") || k.includes("pyran")) return "weather_station";
  if (k.includes("sensor") || k.includes("irradiance")) return "sensor";
  if (k.includes("meter")) return "meter";
  if (k.includes("log")) return "logger";
  if (k.includes("gateway") || k.includes("rtu")) return "gateway";
  if (k.includes("module") || k.includes("panel")) return "module";
  if (k.includes("storage") || k.includes("bess") || k.includes("battery")) return "storage";
  if (k.includes("breaker") || k.includes("acb") || k.includes("vcb") || k.includes("mccb")) return "breaker";
  if (k.includes("load")) return "load";
  return "device";
}

export function equipmentClassLabel(raw?: string | null): string {
  return CLASS_LABEL[equipmentClass(raw)];
}

/* Line-art paths, each drawn in a 24×24 box with `currentColor` strokes. */
const PATHS: Record<EquipmentClass, ReactNode> = {
  // DC in → AC out: a box with a sine over a step.
  inverter: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M6 14c1.5-3 3-3 4 0s2.5 3 4 0" />
      <path d="M6 9h4M16 9h2M17 8v2" />
    </>
  ),
  // Meter: a dial.
  meter: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 12l4-2.5" />
      <path d="M12 4v1.5M20 12h-1.5M12 20v-1.5M4 12h1.5" />
    </>
  ),
  // Combiner / SCB: many strings into one bus.
  combiner: (
    <>
      <path d="M4 6h6M4 12h6M4 18h6" />
      <path d="M10 6c4 0 4 6 8 6M10 12h8M10 18c4 0 4-6 8-6" />
      <circle cx="19" cy="12" r="1.4" />
    </>
  ),
  // String: cells in a line.
  string: (
    <>
      <rect x="3" y="9" width="4" height="6" rx="1" />
      <rect x="10" y="9" width="4" height="6" rx="1" />
      <rect x="17" y="9" width="4" height="6" rx="1" />
      <path d="M7 12h3M14 12h3" />
    </>
  ),
  // Transformer: two coupled coils.
  transformer: (
    <>
      <circle cx="8.5" cy="12" r="4.5" />
      <circle cx="15.5" cy="12" r="4.5" />
    </>
  ),
  // Sensor: a probe with waves.
  sensor: (
    <>
      <circle cx="12" cy="12" r="2.5" />
      <path d="M7.5 7.5a6.4 6.4 0 000 9M16.5 7.5a6.4 6.4 0 010 9" />
    </>
  ),
  // Weather station: sun over a mast.
  weather_station: (
    <>
      <circle cx="12" cy="8" r="3" />
      <path d="M12 2v1.5M12 12.5V14M6 8H4.5M19.5 8H18M7.8 3.8l-1 -1M17.2 3.8l1 -1" />
      <path d="M12 14v6M8 20h8" />
    </>
  ),
  // Logger: a stack of records.
  logger: (
    <>
      <rect x="4" y="5" width="16" height="4" rx="1" />
      <rect x="4" y="11" width="16" height="4" rx="1" />
      <path d="M7 7h.01M7 13h.01" />
      <path d="M6 17h12" />
    </>
  ),
  // Gateway: a hub with uplink.
  gateway: (
    <>
      <rect x="4" y="13" width="16" height="6" rx="1.5" />
      <path d="M7 16h.01M11 16h.01" />
      <path d="M12 13V8M9 8a3 3 0 016 0" />
    </>
  ),
  // Module / panel: a grid.
  module: (
    <>
      <rect x="3" y="5" width="18" height="12" rx="1" />
      <path d="M9 5v12M15 5v12M3 9h18M3 13h18" />
      <path d="M11 17l-1 3M13 17l1 3" />
    </>
  ),
  // Storage / BESS: a battery cell with charge level.
  storage: (
    <>
      <rect x="6" y="4" width="12" height="16" rx="1.5" />
      <rect x="9" y="2" width="6" height="2" rx="0.5" />
      <path d="M9 13h6M9 17h6" />
      <path d="M12 7v3M10.5 8.5h3" />
    </>
  ),
  // Breaker: a switch in open position.
  breaker: (
    <>
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="19" r="2" />
      <path d="M12 7v1" />
      <path d="M12 8l4 6" />
      <path d="M12 17v-3" />
    </>
  ),
  // Load: a zigzag (resistor symbol) between terminals.
  load: (
    <>
      <path d="M4 12h3" />
      <path d="M7 12l1.5-3 2 6 2-6 2 6 1.5-3" />
      <path d="M16 12h4" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="20" cy="12" r="1" />
    </>
  ),
  // Generic device.
  device: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
};

export function EquipmentIcon(props: {
  deviceClass?: string | null;
  /** A labelled image standing alone; omit when a text label sits beside it. */
  title?: string;
  size?: number;
}): ReactNode {
  const cls = equipmentClass(props.deviceClass);
  const size = props.size ?? 20;
  const labelled = props.title !== undefined;
  return (
    <svg
      className="sk-equip-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={labelled ? "img" : undefined}
      aria-label={labelled ? (props.title || equipmentClassLabel(props.deviceClass)) : undefined}
      aria-hidden={labelled ? undefined : true}
    >
      {labelled ? <title>{props.title || equipmentClassLabel(props.deviceClass)}</title> : null}
      {PATHS[cls]}
    </svg>
  );
}
